import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "../components/Topbar";
import { useAuth, dashboardPathForRole } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { User, Save, KeyRound, Loader2, Building2, ArrowLeft } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Profile() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changing, setChanging] = useState(false);

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    fiscal_code: "",
    birth_date: "",
    phone: "",
  });

  const [pwForm, setPwForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const isAzienda = user?.role === "azienda";
  const [savingEmail, setSavingEmail] = useState(false);
  const [accessEmail, setAccessEmail] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/me/profile");
        setForm({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          fiscal_code: data.fiscal_code || "",
          birth_date: data.birth_date || "",
          phone: data.phone || "",
        });
        setAccessEmail(data.email || "");
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/me/profile", {
        first_name: form.first_name,
        last_name: form.last_name,
        fiscal_code: form.fiscal_code || null,
        birth_date: form.birth_date || null,
        phone: form.phone || null,
      });
      toast.success("Profilo aggiornato con successo");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePw = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error("Le password non coincidono");
      return;
    }
    setChanging(true);
    try {
      await api.post("/me/change-password", pwForm);
      toast.success("Password aggiornata con successo");
      setPwForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setChanging(false);
    }
  };

  const handleSaveEmail = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try {
      await api.patch("/me/profile", { email: accessEmail });
      toast.success("Email di accesso aggiornata");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingEmail(false);
    }
  };

  const isStudente = user?.role === "studente";

  // Campi del cambio password, riusati sia nella scheda unificata dell'azienda
  // sia nella scheda a sé degli altri ruoli.
  const passwordFields = (
    <>
      <div className="grid sm:grid-cols-3 gap-5">
        <div>
          <Label className="text-sm">Password attuale</Label>
          <Input
            data-testid="pw-current"
            type="password"
            value={pwForm.current_password}
            onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
            className="mt-1.5 h-11 rounded-sm"
            required
          />
        </div>
        <div>
          <Label className="text-sm">Nuova password</Label>
          <Input
            data-testid="pw-new"
            type="password"
            minLength={8}
            value={pwForm.new_password}
            onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
            className="mt-1.5 h-11 rounded-sm"
            required
          />
        </div>
        <div>
          <Label className="text-sm">Conferma nuova password</Label>
          <Input
            data-testid="pw-confirm"
            type="password"
            minLength={8}
            value={pwForm.confirm_password}
            onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })}
            className="mt-1.5 h-11 rounded-sm"
            required
          />
        </div>
      </div>
      <div className="flex justify-end mt-8">
        <button
          type="submit"
          disabled={changing}
          data-testid="change-password-btn"
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
        >
          {changing ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />}
          Aggiorna password
        </button>
      </div>
    </>
  );

  return (
    <div data-testid="profile-page" className="min-h-screen bg-brand-bg">
      <Topbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <button
          type="button"
          onClick={() => navigate(dashboardPathForRole(user?.role))}
          data-testid="profile-back-home"
          className="inline-flex items-center gap-1.5 text-sm text-brand-blue hover:underline underline-offset-4 mb-6"
        >
          <ArrowLeft size={16} /> Torna alla home
        </button>

        <div className="mb-10">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-gold mb-2">
            <User size={14} /> Il mio profilo
          </div>
          <h1 className="text-3xl sm:text-4xl text-brand-ink mb-2">Impostazioni account</h1>
          <p className="text-brand-muted max-w-2xl">
            {isAzienda
              ? "Gestisci i tuoi dati di accesso: email e password. I dati aziendali si modificano dall’area riservata."
              : "Modifica i tuoi dati personali e cambia la password. L’email e il ruolo non sono modificabili in autonomia."}
          </p>
        </div>

        <div className="grid gap-6">
          {/* Azienda: scheda unica "Dati di accesso" con email + password */}
          {isAzienda && (
            <div
              data-testid="access-card"
              className="bg-white border border-brand-line rounded-sm p-6 sm:p-8"
            >
              <div className="flex items-center gap-2 mb-1">
                <Building2 size={18} className="text-brand-blue" />
                <h2 className="text-xl text-brand-blue">Dati di accesso</h2>
              </div>
              <p className="text-sm text-brand-muted mb-6">
                L’azienda accede al portale con questa email e la password.
              </p>

              {/* Email */}
              <form onSubmit={handleSaveEmail} data-testid="access-form">
                <div className="max-w-md">
                  <Label className="text-sm">Email di accesso</Label>
                  <Input
                    data-testid="access-email"
                    type="email"
                    value={accessEmail}
                    onChange={(e) => setAccessEmail(e.target.value)}
                    className="mt-1.5 h-11 rounded-sm"
                    required
                  />
                </div>
                <div className="flex justify-end mt-6">
                  <button type="submit" disabled={savingEmail || loading}
                    data-testid="access-save-btn"
                    className="btn-primary inline-flex items-center gap-2 disabled:opacity-60">
                    {savingEmail ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    Salva email
                  </button>
                </div>
              </form>

              <div className="border-t border-brand-line my-8" />

              {/* Password */}
              <h3 className="text-base font-semibold text-brand-ink mb-1">Cambia password</h3>
              <p className="text-sm text-brand-muted mb-6">
                La nuova password deve essere di almeno 8 caratteri e diversa dall’attuale.
              </p>
              <form onSubmit={handleChangePw} data-testid="change-password-form">
                {passwordFields}
              </form>
            </div>
          )}

          {/* Dati personali — non mostrati per l'azienda (nessun dato di referente) */}
          {!isAzienda && (
          <form
            onSubmit={handleSave}
            data-testid="profile-form"
            className="bg-white border border-brand-line rounded-sm p-6 sm:p-8"
          >
            <h2 className="text-xl text-brand-blue mb-1">Dati personali</h2>
            <p className="text-sm text-brand-muted mb-6">
              Email: <span className="font-semibold text-brand-ink">{user?.email}</span>
              &nbsp;·&nbsp; Ruolo: <span className="font-semibold text-brand-ink">{user?.role}</span>
            </p>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-sm">Nome</Label>
                <Input
                  data-testid="profile-first-name"
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  className="mt-1.5 h-11 rounded-sm"
                  required
                />
              </div>
              <div>
                <Label className="text-sm">Cognome</Label>
                <Input
                  data-testid="profile-last-name"
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  className="mt-1.5 h-11 rounded-sm"
                  required
                />
              </div>
              {!isAzienda && (
                <div>
                  <Label className="text-sm">
                    Codice fiscale {isStudente && <span className="text-brand-gold">(consigliato)</span>}
                  </Label>
                  <Input
                    data-testid="profile-fiscal-code"
                    value={form.fiscal_code}
                    onChange={(e) => setForm({ ...form, fiscal_code: e.target.value.toUpperCase() })}
                    placeholder="RSSMRA85M01H501U"
                    maxLength={16}
                    className="mt-1.5 h-11 rounded-sm font-mono uppercase"
                  />
                </div>
              )}
              {!isAzienda && (
                <div>
                  <Label className="text-sm">Data di nascita</Label>
                  <Input
                    data-testid="profile-birth-date"
                    type="date"
                    value={form.birth_date}
                    onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
                    className="mt-1.5 h-11 rounded-sm"
                  />
                </div>
              )}
              <div className="sm:col-span-2">
                <Label className="text-sm">Telefono</Label>
                <Input
                  data-testid="profile-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+39 333 1234567"
                  className="mt-1.5 h-11 rounded-sm"
                />
              </div>
            </div>

            <div className="flex justify-end mt-8">
              <button
                type="submit"
                disabled={saving || loading}
                data-testid="profile-save-btn"
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Salva modifiche
              </button>
            </div>
          </form>
          )}

          {/* Cambio password — scheda a sé per i ruoli non azienda */}
          {!isAzienda && (
          <form
            onSubmit={handleChangePw}
            data-testid="change-password-form"
            className="bg-white border border-brand-line rounded-sm p-6 sm:p-8"
          >
            <h2 className="text-xl text-brand-blue mb-1">Cambio password</h2>
            <p className="text-sm text-brand-muted mb-6">
              La nuova password deve essere di almeno 8 caratteri e diversa dall’attuale.
            </p>
            {passwordFields}
          </form>
          )}
        </div>
      </main>
    </div>
  );
}
